<template>
  <div class="delivery-calendar-wrapper">
    <!-- =====================================================
         API LOADING OVERLAY
         ===================================================== -->
    <transition name="loading-overlay">
      <div v-if="apiLoading" class="loading-overlay">
        <div class="loading-card">
          <div class="loading-animation">
            <div class="loading-ring">
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="loading-logo">
                <i class="fas fa-truck"></i>
              </div>
            </div>
          </div>
          <div class="loading-text">
            <h3>{{ loadingMessage }}</h3>
            <div class="loading-dots">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
          <div class="loading-progress">
            <div
              class="progress-bar"
              :style="{ width: loadingProgress + '%' }"
            ></div>
          </div>
          <p class="tip-text">
            {{ currentTip }}
          </p>
        </div>
      </div>
    </transition>

    <!-- =====================================================
         PAGE HEADER
         ===================================================== -->
    <div class="page-header">
      <div class="page-heading">
        <div class="page-icon">
          <i class="fas fa-truck"></i>
        </div>
        <div>
          <h1>Delivery Calendar</h1>
          <p>
            Manage and monitor your furniture deliveries
          </p>
        </div>
      </div>
      <div class="header-controls">
        <div class="control-wrapper">
          <label>Month</label>
          <input
            type="month"
            v-model="selectedMonth"
            @change="onMonthChange"
            class="month-picker-input"
          />
        </div>
        <div
          v-if="packet.userDetails && packet.userDetails.userrole === 'admin'"
          class="control-wrapper"
        >
          <label>Location</label>
          <select
            v-model="location_id"
            @change="onLocationChange"
            class="location-select"
          >
            <option
              v-for="opt in location_options"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.text }}
            </option>
          </select>
        </div>
        <button
          @click="fetchDeliveryCounts"
          :disabled="apiLoading"
          class="refresh-btn"
        >
          <span
            v-if="apiLoading"
            class="spinner-sm"
          ></span>
          <i
            v-else
            class="fas fa-sync-alt"
          ></i>
          Refresh
        </button>
      </div>
    </div>

    <!-- =====================================================
         MONTH SUMMARY
         ===================================================== -->
    <div
      v-if="deliveryData && calendarDays.length > 0"
      class="summary-grid"
    >
      <!-- Trips -->
      <div class="summary-card">
        <div class="summary-icon trips">
          <i class="fas fa-truck"></i>
        </div>
        <div class="summary-content">
          <span>Total Trips</span>
          <strong>{{ summaryStats.trips }}</strong>
          <small>For selected month</small>
        </div>
      </div>

      <!-- Scheduled -->
      <div class="summary-card">
        <div class="summary-icon scheduled">
          <i class="fas fa-box"></i>
        </div>
        <div class="summary-content">
          <span>Scheduled</span>
          <strong>{{ summaryStats.scheduled }}</strong>
          <small>Scheduled deliveries</small>
        </div>
      </div>

      <!-- Unassigned -->
      <div class="summary-card">
        <div class="summary-icon unassigned">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <div class="summary-content">
          <span>Un-Assigned</span>
          <strong>{{ summaryStats.unassigned }}</strong>
          <small>Need assignment</small>
        </div>
      </div>

      <!-- Invalid -->
      <div class="summary-card">
        <div class="summary-icon invalid">
          <i class="fas fa-map-marker-alt"></i>
        </div>
        <div class="summary-content">
          <span>Invalid</span>
          <strong>{{ summaryStats.invalid }}</strong>
          <small>Address issues</small>
        </div>
      </div>
    </div>

    <!-- =====================================================
         LOADING
         ===================================================== -->
    <div
      v-if="loading && !apiLoading"
      class="state-card"
    >
      <div class="state-spinner"></div>
      <strong>Loading delivery calendar...</strong>
    </div>

    <!-- =====================================================
         ERROR
         ===================================================== -->
    <div
      v-else-if="apiError"
      class="state-card error-state"
    >
      <div class="state-icon">
        <i class="fas fa-exclamation-circle"></i>
      </div>
      <strong>Unable to load delivery data</strong>
      <p>{{ apiError }}</p>
      <button
        class="retry-btn"
        @click="fetchDeliveryCounts"
      >
        <i class="fas fa-redo"></i>
        Try Again
      </button>
    </div>

    <!-- =====================================================
         CALENDAR
         ===================================================== -->
    <div
      v-else-if="deliveryData && calendarDays.length > 0"
      class="calendar-card"
    >
      <!-- Calendar Header -->
      <div class="calendar-header">
        <div>
          <h2>
            {{ formatMonthYear(selectedMonth) }}
          </h2>
          <p>
            Furniture delivery schedule
            <span v-if="location_id !== 90">
              · {{ location_name }}
            </span>
          </p>
        </div>
        <div class="calendar-header-right">
          <div class="calendar-status">
            <span class="status-dot blue"></span>
            Trips
          </div>
          <div class="calendar-status">
            <span class="status-dot cyan"></span>
            Scheduled
          </div>
          <div class="calendar-status">
            <span class="status-dot yellow"></span>
            Unassigned
          </div>
          <div class="calendar-status">
            <span class="status-dot red"></span>
            Invalid
          </div>
        </div>
      </div>

      <!-- Weekdays -->
      <div class="calendar-row weekdays">
        <div>Sun</div>
        <div>Mon</div>
        <div>Tue</div>
        <div>Wed</div>
        <div>Thu</div>
        <div>Fri</div>
        <div>Sat</div>
      </div>

      <!-- Days -->
      <div class="calendar-body">
        <div
          v-for="(day, index) in calendarDays"
          :key="day.date"
          class="calendar-day"
          :class="{
            'other-month': !day.isCurrentMonth,
            'today': day.isToday,
            'has-deliveries': day.deliveryCount > 0
          }"
          :style="{
            animationDelay: `${index * 0.015}s`
          }"
          @click="day.deliveryCount > 0 && showDeliveryDetails(day)"
        >
          <!-- Day Header -->
          <div class="day-header">
            <span class="day-number">
              {{ day.day }}
            </span>
            <span
              v-if="day.isToday"
              class="today-badge"
            >
              TODAY
            </span>
          </div>

          <!-- Delivery Information -->
          <div
            v-if="day.deliveryCount > 0"
            class="day-stats"
          >
            <!-- Trips -->
            <div class="stat-item trips-stat">
              <div class="stat-icon">
                <i class="fas fa-truck"></i>
              </div>
              <div class="stat-label">
                Trips
              </div>
              <strong>
                {{ day.trips || 0 }}
              </strong>
            </div>

            <!-- Scheduled -->
            <div class="stat-item scheduled-stat">
              <div class="stat-icon">
                <i class="fas fa-box"></i>
              </div>
              <div class="stat-label">
                Scheduled
              </div>
              <strong>
                {{ day.deliveryCount }}
              </strong>
            </div>

            <!-- Unassigned -->
            <div
              v-if="day.unassigned > 0"
              class="stat-item warning-stat"
            >
              <div class="stat-icon">
                <i class="fas fa-exclamation-triangle"></i>
              </div>
              <div class="stat-label">
                Unassigned
              </div>
              <strong>
                {{ day.unassigned }}
              </strong>
            </div>

            <!-- Invalid -->
            <div
              v-if="day.invalidAddresses > 0"
              class="stat-item invalid-stat"
            >
              <div class="stat-icon">
                <i class="fas fa-map-marker-alt"></i>
              </div>
              <div class="stat-label">
                Invalid
              </div>
              <strong>
                {{ day.invalidAddresses }}
              </strong>
            </div>
          </div>

          <!-- Empty Day -->
          <div
            v-else
            class="empty-day"
          >
            <span>No deliveries</span>
          </div>

          <!-- Click hint -->
          <div
            v-if="day.deliveryCount > 0"
            class="day-hover-hint"
          >
            View orders
            <i class="fas fa-arrow-right"></i>
          </div>
        </div>
      </div>

      <!-- Calendar Footer -->
      <div class="calendar-footer">
        <span>
          <i class="fas fa-info-circle"></i>
          Click any delivery day to view orders
        </span>
        <span>
          {{ calendarDays.filter(day => day.isCurrentMonth).length }}
          days displayed
        </span>
      </div>
    </div>

    <!-- =====================================================
         NO DATA
         ===================================================== -->
    <div
      v-else-if="!loading && !deliveryData && !apiError"
      class="state-card"
    >
      <div class="state-icon">
        <i class="fas fa-calendar-times"></i>
      </div>
      <strong>No delivery data available</strong>
      <p>
        Select a month and refresh the calendar.
      </p>
    </div>

    <!-- =====================================================
         DELIVERY DETAILS MODAL
         ===================================================== -->
    <div
      v-if="selectedDay"
      class="modal-overlay"
      @click="closeModal"
    >
      <div
        class="modal-content"
        @click.stop
      >
        <!-- Modal Header -->
        <div class="modal-header">
          <div class="modal-title-wrapper">
            <div class="modal-title-icon">
              <i class="fas fa-truck"></i>
            </div>
            <div>
              <h3>
                {{ formatDate(selectedDay.date) }}
              </h3>
              <p>
                Delivery orders
              </p>
            </div>
          </div>
          <button
            class="close-btn"
            @click="closeModal"
            aria-label="Close"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- =================================================
             LOADING ORDERS
             ================================================= -->
        <div
          v-if="selectedDay.loadingOrders"
          class="truck-loading-container"
        >
          <div class="truck-loading-scene">
            <div class="warehouse">
              <div class="warehouse-roof"></div>
              <div class="warehouse-door"></div>
              <span>DELIVERY</span>
            </div>
            <div class="loading-package package-one">
              <i class="fas fa-box"></i>
            </div>
            <div class="loading-package package-two">
              <i class="fas fa-box"></i>
            </div>
            <div class="loading-package package-three">
              <i class="fas fa-box"></i>
            </div>
            <div class="animated-truck">
              <div class="truck-body"></div>
              <div class="truck-cabin">
                <div class="truck-window"></div>
              </div>
              <div class="truck-wheel truck-wheel-one"></div>
              <div class="truck-wheel truck-wheel-two"></div>
              <div class="truck-light"></div>
            </div>
            <div class="loading-road"></div>
            <div class="truck-loading-message">
              <i class="fas fa-truck"></i>
              Loading delivery orders
              <span class="loading-dots-inline">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </div>
          </div>
        </div>

        <!-- =================================================
             ERROR LOADING ORDERS
             ================================================= -->
        <div
          v-else-if="selectedDay.ordersError"
          class="modal-error"
        >
          <div class="modal-error-icon">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <strong>
            Error loading orders
          </strong>
          <p>
            {{ selectedDay.ordersError }}
          </p>
        </div>

        <!-- =================================================
             ORDERS
             ================================================= -->
        <div
          v-else-if="
            selectedDay.orders &&
            selectedDay.orders.length > 0
          "
          class="orders-section"
        >
          <!-- Filters -->
          <div class="filter-panel">
            <div class="filter-panel-header">
              <div>
                <h4>
                  <i class="fas fa-filter"></i>
                  Order Filters
                </h4>
                <span v-if="getActiveFiltersCount > 0">
                  {{ getActiveFiltersCount }} active
                </span>
              </div>
              <button
                class="clear-filters-btn"
                @click="clearFilters"
              >
                <i class="fas fa-times"></i>
                Clear Filters
              </button>
            </div>

            <div class="filter-grid">
              <!-- Invoice -->
              <div class="filter-group">
                <label>
                  Invoice No
                </label>
                <div class="input-with-icon">
                  <i class="fas fa-file-invoice"></i>
                  <input
                    type="text"
                    v-model="orderFilters.orderNo"
                    placeholder="Search invoice..."
                    class="filter-input"
                  />
                </div>
              </div>

              <!-- Customer -->
              <div class="filter-group">
                <label>
                  Customer
                </label>
                <div class="input-with-icon">
                  <i class="fas fa-user"></i>
                  <input
                    type="text"
                    v-model="orderFilters.customerName"
                    placeholder="Customer name..."
                    class="filter-input"
                  />
                </div>
              </div>

              <!-- Phone -->
              <div class="filter-group">
                <label>
                  Phone
                </label>
                <div class="input-with-icon">
                  <i class="fas fa-phone"></i>
                  <input
                    type="text"
                    v-model="orderFilters.phone"
                    placeholder="Phone number..."
                    class="filter-input"
                  />
                </div>
              </div>

              <!-- Status -->
              <div class="filter-group">
                <label>
                  Status
                </label>
                <div class="input-with-icon">
                  <i class="fas fa-tag"></i>
                  <select
                    v-model="orderFilters.status"
                    class="filter-input"
                  >
                    <option value="">
                      All Statuses
                    </option>
                    <option
                      v-for="status in uniqueStatuses"
                      :key="status"
                      :value="status"
                    >
                      {{ status }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Filter summary -->
            <div
              v-if="
                filteredOrders.length !==
                selectedDay.orders.length
              "
              class="filter-result"
            >
              <i class="fas fa-search"></i>
              Showing
              <strong>{{ filteredOrders.length }}</strong>
              of
              <strong>{{ selectedDay.orders.length }}</strong>
              orders
            </div>
          </div>

          <!-- Table -->
          <div class="table-container">
            <table class="details-table">
              <thead>
                <tr>
                  <th>Invoice No</th>
                  <th>Customer</th>
                  <th>Address</th>
                  <th>Phone</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(order, idx) in filteredOrders"
                  :key="idx"
                  class="fade-in-row"
                  :style="{
                    animationDelay: `${idx * 0.015}s`
                  }"
                >
                  <!-- Invoice -->
                  <td>
                    <span class="invoice-number">
                      {{ order.order_no || '-' }}
                    </span>
                  </td>

                  <!-- Customer -->
                  <td>
                    <div class="customer-cell">
                      <div class="customer-avatar">
                        <i class="fas fa-user"></i>
                      </div>
                      <span>
                        {{ order.ship_to_name || '-' }}
                      </span>
                    </div>
                  </td>

                  <!-- Address -->
                  <td>
                    <div class="address-cell">
                      {{ order.ship_to_address || '-' }}
                    </div>
                  </td>

                  <!-- Phone -->
                  <td>
                    {{ order.phone || order.ship_to_phone || '-' }}
                  </td>

                  <!-- Status -->
                  <td>
                    <span
                      :class="
                        'status-badge status-' +
                        (order.status || '')
                          .toLowerCase()
                          .replace(/ /g, '-')
                      "
                    >
                      {{ order.status || '-' }}
                    </span>
                  </td>
                </tr>

                <!-- No results -->
                <tr
                  v-if="filteredOrders.length === 0"
                  class="no-results-row"
                >
                  <td colspan="7">
                    <div class="no-results">
                      <i class="fas fa-search"></i>
                      <strong>
                        No orders found
                      </strong>
                      <span>
                        Try adjusting your filters.
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- =================================================
             NO DETAILS
             ================================================= -->
        <div
          v-else-if="
            !selectedDay.loadingOrders &&
            !selectedDay.ordersError
          "
          class="no-details"
        >
          <div class="no-details-icon">
            <i class="fas fa-box-open"></i>
          </div>
          <strong>
            No order information available
          </strong>
          <span>
            There are no orders associated with this date.
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import Requestor from '@/utilities/Requestor'

const Lightpick = require('lightpick')

import 'lightpick/css/lightpick.css'

export default {
  name: 'DeliveryCalendar',

  data() {
    return {
      packet: {},
      loading: false,
      deliveryData: null,
      apiError: null,
      selectedMonth: moment().format('YYYY-MM'),
      selectedMonthDisplay: moment().format('MMMM YYYY'),
      location_id: 90,
      location_name: '',
      location_options: [
        {
          value: 90,
          text: 'ALL LOCATIONS'
        }
      ],
      selectedDay: null,
      monthPicker: null,
      requestedDatePickers: {},
      scheduleDatePickers: {},

      orderFilters: {
        invoiceId: '',
        orderNo: '',
        requestedDate: '',
        requestedDateStart: '',
        requestedDateEnd: '',
        scheduleDate: '',
        scheduleDateStart: '',
        scheduleDateEnd: '',
        customerName: '',
        address: '',
        phone: '',
        status: ''
      },

      /* Loading animation */
      apiLoading: false,
      loadingMessage: 'Loading Calendar Data',
      loadingProgress: 0,
      currentTipIndex: 0,
      loadingInterval: null,
      tipInterval: null,
      msgInterval: null,
      apiCallStack: 0,

      tips: [
        'Loading calendar data...',
        'Fetching delivery schedules...',
        'Retrieving trip information...',
        'Processing delivery counts...',
        'Checking delivery addresses...',
        'Syncing with server...',
        'Organizing calendar view...',
        'Preparing statistics...'
      ]
    }
  },

  props: {
    selectedLocationId: {
      type: Number,
      default: null
    },
    locationName: {
      type: String,
      default: ''
    }
  },

  computed: {
    /* =====================================================
       CALENDAR DAYS
       ===================================================== */
    calendarDays() {
      if (
        !this.deliveryData ||
        !this.deliveryData.deliveries_by_date ||
        !this.selectedMonth
      ) {
        return []
      }

      const [year, month] = this.selectedMonth.split('-').map(Number)
      const firstDayOfMonth = new Date(year, month - 1, 1)
      const startDayOfWeek = firstDayOfMonth.getDay()
      const startDate = new Date(year, month - 1, 1 - startDayOfWeek)
      const lastDayOfMonth = new Date(year, month, 0)
      const endDayOfWeek = lastDayOfMonth.getDay()
      const endDate = new Date(year, month, 6 - endDayOfWeek)
      const days = []
      let currentDate = new Date(startDate)
      const today = new Date()
      const todayStr = `${today.getFullYear()}-${String(
        today.getMonth() + 1
      ).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

      while (currentDate <= endDate) {
        const yearNum = currentDate.getFullYear()
        const monthNum = currentDate.getMonth() + 1
        const dayNum = currentDate.getDate()
        const dateStr = `${yearNum}-${String(monthNum).padStart(
          2,
          '0'
        )}-${String(dayNum).padStart(2, '0')}`

        const info = this.deliveryData.deliveries_by_date[dateStr] || {}

        days.push({
          date: dateStr,
          day: dayNum,
          isCurrentMonth: monthNum === month,
          isToday: dateStr === todayStr,
          deliveryCount: info.count || 0,
          trips: info.trips || 0,
          unassigned: info.unassigned || 0,
          invalidAddresses: info.invalid_addresses || 0,
          details: info.details || []
        })

        currentDate.setDate(currentDate.getDate() + 1)
      }

      return days
    },

    /* =====================================================
       MONTH SUMMARY
       ===================================================== */
    summaryStats() {
      const days = this.calendarDays.filter(day => day.isCurrentMonth)

      return {
        trips: days.reduce((total, day) => total + Number(day.trips || 0), 0),
        scheduled: days.reduce(
          (total, day) => total + Number(day.deliveryCount || 0),
          0
        ),
        unassigned: days.reduce(
          (total, day) => total + Number(day.unassigned || 0),
          0
        ),
        invalid: days.reduce(
          (total, day) => total + Number(day.invalidAddresses || 0),
          0
        )
      }
    },

    /* =====================================================
       FILTERED ORDERS
       ===================================================== */
    filteredOrders() {
      if (!this.selectedDay || !this.selectedDay.orders) {
        return []
      }

      const filters = this.orderFilters

      return this.selectedDay.orders.filter(order => {
        if (filters.invoiceId) {
          const invoiceVal = (order.invoice_id || order.invoice_no || '')
            .toString()
            .toLowerCase()

          if (!invoiceVal.includes(filters.invoiceId.toLowerCase())) {
            return false
          }
        }

        if (filters.orderNo) {
          const orderNoVal = (order.order_no || '').toString().toLowerCase()

          if (!orderNoVal.includes(filters.orderNo.toLowerCase())) {
            return false
          }
        }

        if (filters.requestedDateStart && filters.requestedDateEnd) {
          const reqDateVal = order.requested_delivery_date || ''

          if (
            reqDateVal < filters.requestedDateStart ||
            reqDateVal > filters.requestedDateEnd
          ) {
            return false
          }
        } else if (filters.requestedDateStart) {
          const reqDateVal = order.requested_delivery_date || ''

          if (reqDateVal !== filters.requestedDateStart) {
            return false
          }
        }

        if (filters.scheduleDateStart && filters.scheduleDateEnd) {
          const schDateVal = order.schedule_date || ''

          if (
            schDateVal < filters.scheduleDateStart ||
            schDateVal > filters.scheduleDateEnd
          ) {
            return false
          }
        } else if (filters.scheduleDateStart) {
          const schDateVal = order.schedule_date || ''

          if (schDateVal !== filters.scheduleDateStart) {
            return false
          }
        }

        if (filters.customerName) {
          const nameVal = (order.ship_to_name || '').toLowerCase()

          if (!nameVal.includes(filters.customerName.toLowerCase())) {
            return false
          }
        }

        if (filters.address) {
          const addressVal = (order.ship_to_address || '').toLowerCase()

          if (!addressVal.includes(filters.address.toLowerCase())) {
            return false
          }
        }

        if (filters.phone) {
          const phoneVal = (order.phone || order.ship_to_phone || '')
            .toString()
            .toLowerCase()

          if (!phoneVal.includes(filters.phone.toLowerCase())) {
            return false
          }
        }

        if (filters.status) {
          const statusVal = (order.status || '').toLowerCase()

          if (statusVal !== filters.status.toLowerCase()) {
            return false
          }
        }

        return true
      })
    },

    /* =====================================================
       UNIQUE STATUSES
       ===================================================== */
    uniqueStatuses() {
      if (!this.selectedDay || !this.selectedDay.orders) {
        return []
      }

      const statuses = new Set()

      this.selectedDay.orders.forEach(order => {
        if (order.status && order.status.trim()) {
          statuses.add(order.status)
        }
      })

      return Array.from(statuses).sort()
    },

    /* =====================================================
       DATE RANGE STATE
       ===================================================== */
    isRequestedDateRangeActive() {
      return !!(
        this.orderFilters.requestedDateStart &&
        this.orderFilters.requestedDateEnd
      )
    },

    isScheduleDateRangeActive() {
      return !!(
        this.orderFilters.scheduleDateStart &&
        this.orderFilters.scheduleDateEnd
      )
    },

    /* =====================================================
       ACTIVE FILTER COUNT
       ===================================================== */
    getActiveFiltersCount() {
      let count = 0

      if (this.orderFilters.invoiceId) count++
      if (this.orderFilters.orderNo) count++
      if (this.orderFilters.requestedDateStart) count++
      if (this.orderFilters.scheduleDateStart) count++
      if (this.orderFilters.customerName) count++
      if (this.orderFilters.address) count++
      if (this.orderFilters.phone) count++
      if (this.orderFilters.status) count++

      return count
    },

    /* =====================================================
       LOADING TIP
       ===================================================== */
    currentTip() {
      return this.tips[this.currentTipIndex] || this.tips[0]
    }
  },

  watch: {
    selectedLocationId: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.fetchDeliveryCounts()
        }
      },
      immediate: true
    },

    locationName: {
      handler(newVal) {
        if (newVal) {
          this.location_name = newVal
        }
      },
      immediate: true
    }
  },

  methods: {
    /* =====================================================
       REFRESH
       ===================================================== */
    refreshCalendar() {
      this.fetchDeliveryCounts()
    },

    /* =====================================================
       DAY OF WEEK
       ===================================================== */
    getDayOfWeek(dateString) {
      if (!dateString) return ''

      const [year, month, day] = dateString.split('-').map(Number)
      const date = new Date(year, month - 1, day)

      return date.toLocaleDateString('en-US', {
        weekday: 'long'
      })
    },

    /* =====================================================
       MONTH
       ===================================================== */
    onMonthChange() {
      this.selectedMonthDisplay = moment(this.selectedMonth).format(
        'MMMM YYYY'
      )
      this.fetchDeliveryCounts()
    },

    formatMonthYear(dateStr) {
      return moment(dateStr).format('MMMM YYYY')
    },

    formatDate(dateStr) {
      return moment(dateStr).format('MMMM D, YYYY')
    },

    formatDateRange(start, end) {
      if (!start) return ''

      if (start === end) {
        return moment(start).format('MMM D, YYYY')
      }

      return `${moment(start).format('MMM D')} - ${moment(end).format(
        'MMM D, YYYY'
      )}`
    },

    /* =====================================================
       DATE RANGE CHECK
       ===================================================== */
    isDateInRange(date, start, end) {
      if (!date) return false

      if (start && end) {
        return date >= start && date <= end
      }

      if (start) {
        return date === start
      }

      return false
    },

    /* =====================================================
       CLOSE MODAL
       ===================================================== */
    closeModal() {
      this.destroyDatePickers()
      this.selectedDay = null
      this.clearFilters()
    },

    /* =====================================================
       CLEAR FILTERS
       ===================================================== */
    clearFilters() {
      this.orderFilters = {
        invoiceId: '',
        orderNo: '',
        requestedDate: '',
        requestedDateStart: '',
        requestedDateEnd: '',
        scheduleDate: '',
        scheduleDateStart: '',
        scheduleDateEnd: '',
        customerName: '',
        address: '',
        phone: '',
        status: ''
      }

      if (
        this.selectedDay &&
        this.requestedDatePickers[this.selectedDay.date]
      ) {
        this.requestedDatePickers[this.selectedDay.date].setDate(null)
      }

      if (
        this.selectedDay &&
        this.scheduleDatePickers[this.selectedDay.date]
      ) {
        this.scheduleDatePickers[this.selectedDay.date].setDate(null)
      }
    },

    /* =====================================================
       DESTROY DATE PICKERS
       ===================================================== */
    destroyDatePickers() {
      if (
        this.selectedDay &&
        this.requestedDatePickers[this.selectedDay.date]
      ) {
        this.requestedDatePickers[this.selectedDay.date].destroy()
        delete this.requestedDatePickers[this.selectedDay.date]
      }

      if (
        this.selectedDay &&
        this.scheduleDatePickers[this.selectedDay.date]
      ) {
        this.scheduleDatePickers[this.selectedDay.date].destroy()
        delete this.scheduleDatePickers[this.selectedDay.date]
      }
    },

    /* =====================================================
       INITIALIZE DATE PICKERS
       ===================================================== */
    initModalDatePickers() {
      if (!this.selectedDay) return

      const that = this

      const requestedPickerId = `requested-date-picker-${this.selectedDay.date}`
      const schedulePickerId = `schedule-date-picker-${this.selectedDay.date}`

      const requestedField = document.getElementById(requestedPickerId)
      const scheduleField = document.getElementById(schedulePickerId)

      if (!requestedField || !scheduleField) {
        return
      }

      this.requestedDatePickers[this.selectedDay.date] = new Lightpick({
        field: requestedField,
        singleDate: false,
        repick: true,
        format: 'YYYY-MM-DD',
        dropdowns: {
          years: true,
          months: true
        },
        onSelect: function(start, end) {
          if (start && end) {
            that.orderFilters.requestedDateStart = start.format('YYYY-MM-DD')
            that.orderFilters.requestedDateEnd = end.format('YYYY-MM-DD')
            that.orderFilters.requestedDate = `${start.format(
              'YYYY-MM-DD'
            )} to ${end.format('YYYY-MM-DD')}`
          } else if (start) {
            that.orderFilters.requestedDateStart = start.format('YYYY-MM-DD')
            that.orderFilters.requestedDateEnd = start.format('YYYY-MM-DD')
            that.orderFilters.requestedDate = start.format('YYYY-MM-DD')
          } else {
            that.orderFilters.requestedDateStart = ''
            that.orderFilters.requestedDateEnd = ''
            that.orderFilters.requestedDate = ''
          }
        }
      })

      this.scheduleDatePickers[this.selectedDay.date] = new Lightpick({
        field: scheduleField,
        singleDate: false,
        repick: true,
        format: 'YYYY-MM-DD',
        dropdowns: {
          years: true,
          months: true
        },
        onSelect: function(start, end) {
          if (start && end) {
            that.orderFilters.scheduleDateStart = start.format('YYYY-MM-DD')
            that.orderFilters.scheduleDateEnd = end.format('YYYY-MM-DD')
            that.orderFilters.scheduleDate = `${start.format(
              'YYYY-MM-DD'
            )} to ${end.format('YYYY-MM-DD')}`
          } else if (start) {
            that.orderFilters.scheduleDateStart = start.format('YYYY-MM-DD')
            that.orderFilters.scheduleDateEnd = start.format('YYYY-MM-DD')
            that.orderFilters.scheduleDate = start.format('YYYY-MM-DD')
          } else {
            that.orderFilters.scheduleDateStart = ''
            that.orderFilters.scheduleDateEnd = ''
            that.orderFilters.scheduleDate = ''
          }
        }
      })

      /* Restore existing requested range */
      if (
        this.orderFilters.requestedDateStart &&
        this.orderFilters.requestedDateEnd
      ) {
        if (
          this.orderFilters.requestedDateStart ===
          this.orderFilters.requestedDateEnd
        ) {
          this.requestedDatePickers[this.selectedDay.date].setDate(
            moment(this.orderFilters.requestedDateStart)
          )
        } else {
          this.requestedDatePickers[this.selectedDay.date].setDateRange(
            moment(this.orderFilters.requestedDateStart),
            moment(this.orderFilters.requestedDateEnd)
          )
        }
      }

      /* Restore existing schedule range */
      if (
        this.orderFilters.scheduleDateStart &&
        this.orderFilters.scheduleDateEnd
      ) {
        if (
          this.orderFilters.scheduleDateStart ===
          this.orderFilters.scheduleDateEnd
        ) {
          this.scheduleDatePickers[this.selectedDay.date].setDate(
            moment(this.orderFilters.scheduleDateStart)
          )
        } else {
          this.scheduleDatePickers[this.selectedDay.date].setDateRange(
            moment(this.orderFilters.scheduleDateStart),
            moment(this.orderFilters.scheduleDateEnd)
          )
        }
      }
    },

    /* =====================================================
       LOCATION
       ===================================================== */
    onLocationChange() {
      this.location_name = this.getLocationName(this.location_id)
      this.fetchDeliveryCounts()
    },

    getLocationName(locationId) {
      if (!locationId) {
        return 'N/A'
      }

      if (
        this.packet.locationsdict &&
        this.packet.locationsdict[locationId]
      ) {
        return this.packet.locationsdict[locationId]
      }

      return `Location ${locationId}`
    },

    /* =====================================================
       SHOW DELIVERY DETAILS
       ===================================================== */
    async showDeliveryDetails(day) {
      this.selectedDay = {
        date: day.date,
        deliveryCount: day.deliveryCount,
        trips: day.trips,
        unassigned: day.unassigned,
        invalidAddresses: day.invalidAddresses,
        orders: [],
        loadingOrders: true,
        ordersError: null
      }

      this.clearFilters()

      await new Promise(resolve => setTimeout(resolve, 600))

      try {
        const locationId = this.selectedLocationId || this.location_id
        const locationNameForUrl = this.locationName || this.location_name

        let url = `/api/sales/envio_orders_by_date?company_id=90&date=${day.date}&type=home_delivery`

        if (locationId && locationId !== 90) {
          url += `&location_id=${locationId}`

          if (locationNameForUrl) {
            url += `&location_name=${encodeURIComponent(locationNameForUrl)}`
          }
        }

        const response = await Requestor.getAsync(url)

        let ordersArray = null

        if (
          response.data &&
          response.data.result &&
          Array.isArray(response.data.result)
        ) {
          ordersArray = response.data.result
        } else if (response.data && Array.isArray(response.data)) {
          ordersArray = response.data
        } else if (Array.isArray(response)) {
          ordersArray = response
        } else if (response.result && Array.isArray(response.result)) {
          ordersArray = response.result
        } else if (
          response.data &&
          response.data.data &&
          response.data.data.result &&
          Array.isArray(response.data.data.result)
        ) {
          ordersArray = response.data.data.result
        }

        if (ordersArray && ordersArray.length > 0) {
          this.selectedDay.orders = ordersArray
          this.selectedDay.loadingOrders = false
        } else {
          this.selectedDay.orders = []
          this.selectedDay.loadingOrders = false
          this.selectedDay.ordersError = null
        }

        this.$nextTick(() => {
          this.initModalDatePickers()
        })
      } catch (err) {
        console.error('Error fetching orders:', err)

        this.selectedDay.loadingOrders = false
        this.selectedDay.ordersError =
          err.message || 'Network error - please check your connection'
        this.selectedDay.orders = []
      }
    },

    /* =====================================================
       API LOADING
       ===================================================== */
    startApiLoading(message = 'Loading Calendar Data') {
      this.apiCallStack++

      if (!this.apiLoading) {
        this.startLoadingAnimation(message)
      } else {
        this.loadingMessage = message
      }
    },

    stopApiLoading() {
      this.apiCallStack--

      if (this.apiCallStack <= 0 && this.apiLoading) {
        this.stopLoadingAnimation(true)
      }
    },

    startLoadingAnimation(message = 'Loading Calendar Data') {
      this.apiLoading = true
      this.loadingMessage = message
      this.loadingProgress = 0
      this.currentTipIndex = 0

      this.loadingInterval = setInterval(() => {
        if (this.loadingProgress < 90) {
          this.loadingProgress += Math.random() * 10

          if (this.loadingProgress > 90) {
            this.loadingProgress = 90
          }
        }
      }, 300)

      this.tipInterval = setInterval(() => {
        if (this.currentTipIndex < this.tips.length - 1) {
          this.currentTipIndex++
        }
      }, 2000)

      const messages = [
        'Loading Calendar Data',
        'Fetching Deliveries',
        'Processing Calendar',
        'Updating Statistics',
        'Finalizing'
      ]

      let msgIndex = 0

      this.msgInterval = setInterval(() => {
        if (msgIndex < messages.length - 1) {
          msgIndex++
          this.loadingMessage = messages[msgIndex]
        }
      }, 1800)
    },

    stopLoadingAnimation(success = true) {
      if (this.loadingInterval) {
        clearInterval(this.loadingInterval)
        this.loadingInterval = null
      }

      if (this.tipInterval) {
        clearInterval(this.tipInterval)
        this.tipInterval = null
      }

      if (this.msgInterval) {
        clearInterval(this.msgInterval)
        this.msgInterval = null
      }

      if (success) {
        this.loadingProgress = 100

        setTimeout(() => {
          this.apiLoading = false
        }, 400)
      } else {
        this.apiLoading = false
      }
    },

    /* =====================================================
       FETCH DELIVERY COUNTS
       ===================================================== */
    async fetchDeliveryCounts() {
      if (!this.selectedMonth) {
        this.selectedMonth = moment().format('YYYY-MM')
      }

      this.startApiLoading('Loading Calendar Data')

      this.loading = true
      this.apiError = null
      this.deliveryData = null

      try {
        const locationId = this.selectedLocationId || this.location_id
        const locationNameForUrl = this.locationName || this.location_name

        let url = `/api/sales/envio_delivery_counts?company_id=90&month=${this.selectedMonth}&type=home_delivery`

        if (locationId && locationId !== 90) {
          url += `&location_id=${locationId}`

          if (locationNameForUrl) {
            url += `&location_name=${encodeURIComponent(locationNameForUrl)}`
          }
        }

        const response = await Requestor.getAsync(url)

        let resultArray = null

        if (
          response.data &&
          response.data.data &&
          response.data.data.data &&
          response.data.data.data.result
        ) {
          resultArray = response.data.data.data.result
        } else if (
          response.data &&
          response.data.data &&
          response.data.data.result
        ) {
          resultArray = response.data.data.result
        } else if (response.data && response.data.result) {
          resultArray = response.data.result
        } else if (response.result) {
          resultArray = response.result
        } else if (response.data && Array.isArray(response.data)) {
          resultArray = response.data
        }

        if (
          resultArray &&
          Array.isArray(resultArray) &&
          resultArray.length > 0
        ) {
          const deliveriesByDate = {}

          for (const item of resultArray) {
            if (item.date) {
              deliveriesByDate[item.date] = {
                count: item.deliveries_by_date || 0,
                trips: item.trips_by_date || 0,
                unassigned: item.unassigned_orders_by_date || 0,
                invalid_addresses: item.invalid_address_orders || 0,
                details: []
              }
            }
          }

          this.deliveryData = {
            deliveries_by_date: deliveriesByDate
          }

          this.apiError = null
        } else {
          this.deliveryData = null
          this.apiError = 'No delivery data found for the selected period'
        }
      } catch (err) {
        console.error('Error fetching delivery data:', err)

        this.apiError =
          err.message || 'Network error - please check your connection'
        this.deliveryData = null
      } finally {
        this.loading = false
        this.stopApiLoading()
      }
    },

    /* =====================================================
       LOAD USER
       ===================================================== */
    loadUser() {
      try {
        const packetData = localStorage.packet || '{}'
        const userDetailsData = localStorage.userDetails || '{}'

        this.packet = JSON.parse(packetData)
        this.packet.userDetails = JSON.parse(userDetailsData)

        if (this.packet.locationsdict) {
          this.location_options = [
            {
              value: 90,
              text: 'ALL LOCATIONS'
            }
          ]

          const excluded = [
            'OUT OF STOCK',
            '101 WH',
            '9TH ST ',
            'ONLINE SALES',
            'ALL',
            'FOLCROFT'
          ]

          for (const [id, name] of Object.entries(this.packet.locationsdict)) {
            if (!excluded.includes(name.toUpperCase())) {
              this.location_options.push({
                value: parseInt(id),
                text: name.toUpperCase()
              })
            }
          }
        }

        this.location_name = this.getLocationName(this.location_id)
      } catch (err) {
        console.error('Error loading user:', err)

        this.packet = {
          userDetails: {}
        }
      }
    }
  },

  /* =======================================================
     CREATED
     ======================================================= */
  created() {
    this.loadUser()
    this.fetchDeliveryCounts()
  },

  /* =======================================================
     MOUNTED
     ======================================================= */
  mounted() {
    document.body.classList.add('calendar-page-loaded')
  },

  /* =======================================================
     BEFORE DESTROY
     ======================================================= */
  beforeDestroy() {
    this.destroyDatePickers()
    document.body.classList.remove('calendar-page-loaded')
    this.stopLoadingAnimation(false)
  }
}
</script>

<style scoped>
/* =========================================================
   ROOT
   ========================================================= */
.delivery-calendar-wrapper {
  --primary: #087fca;
  --primary-dark: #066ba8;
  --primary-light: #eaf6fc;

  --navy: #172b4d;
  --text: #26374a;
  --muted: #718096;

  --border: #e3e9ef;
  --background: #f5f7fa;
  --white: #ffffff;

  --success: #168b68;
  --success-bg: #eaf8f3;

  --warning: #c88700;
  --warning-bg: #fff7df;

  --danger: #d94b4b;
  --danger-bg: #fff0f0;

  width: 100%;
  max-width: 1800px;
  min-height: 100vh;

  margin: 0 auto;
  padding: 24px;

  box-sizing: border-box;

  background: var(--background);

  color: var(--text);

  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    Arial, sans-serif;
}

/* =========================================================
   PAGE HEADER
   ========================================================= */
.page-header {
  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 24px;

  margin-bottom: 18px;

  padding: 20px 22px;
}

.page-heading {
  display: flex;

  align-items: center;

  gap: 14px;
}

.page-icon {
  width: 46px;
  height: 46px;

  display: flex;

  align-items: center;

  justify-content: center;

  color: white;

  background: var(--primary);

  border-radius: 11px;

  font-size: 20px;
}

.page-heading h1 {
  margin: 0 0 4px;

  color: var(--navy);

  font-size: 23px;

  font-weight: 700;

  letter-spacing: -0.4px;
}

.page-heading p {
  margin: 0;

  color: var(--muted);

  font-size: 12px;
}

/* =========================================================
   HEADER CONTROLS
   ========================================================= */
.header-controls {
  display: flex;

  align-items: flex-end;

  gap: 10px;
}

.control-wrapper {
  display: flex;

  flex-direction: column;

  gap: 5px;
}

.control-wrapper label {
  color: #718096;

  font-size: 10px;

  font-weight: 700;

  text-transform: uppercase;

  letter-spacing: 0.5px;
}

.month-picker-input,
.location-select {
  height: 40px;

  min-width: 165px;

  box-sizing: border-box;

  padding: 0 12px;

  color: var(--text);

  background: white;

  border: 1px solid #dce3ea;

  border-radius: 8px;

  font-size: 12px;

  font-weight: 500;

  cursor: pointer;

  transition: all 0.18s ease;
}

.month-picker-input:hover,
.location-select:hover {
  border-color: #b8c5d2;
}

.month-picker-input:focus,
.location-select:focus {
  outline: none;

  border-color: var(--primary);

  box-shadow: 0 0 0 3px rgba(8, 127, 202, 0.09);
}

.refresh-btn {
  height: 40px;

  display: flex;

  align-items: center;

  justify-content: center;

  gap: 7px;

  padding: 0 16px;

  color: white;

  background: var(--primary);

  border: 1px solid var(--primary);

  border-radius: 8px;

  font-size: 12px;

  font-weight: 600;

  cursor: pointer;

  transition: all 0.18s ease;
}

.refresh-btn:hover:not(:disabled) {
  background: var(--primary-dark);

  border-color: var(--primary-dark);

  box-shadow: 0 4px 12px rgba(8, 127, 202, 0.2);
}

.refresh-btn:disabled {
  opacity: 0.6;

  cursor: not-allowed;
}

/* =========================================================
   SUMMARY CARDS
   ========================================================= */
.summary-grid {
  display: grid;

  grid-template-columns: repeat(4, minmax(0, 1fr));

  gap: 14px;

  margin-bottom: 18px;
}

.summary-card {
  display: flex;

  align-items: center;

  gap: 13px;

  min-height: 96px;

  padding: 16px;

  box-sizing: border-box;

  background: white;

  border: 1px solid var(--border);

  border-radius: 12px;

  box-shadow: 0 2px 5px rgba(15, 23, 42, 0.025);

  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.summary-card:hover {
  transform: translateY(-2px);

  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.07);
}

.summary-icon {
  width: 46px;
  height: 46px;

  flex-shrink: 0;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 10px;

  font-size: 28px;
}

.summary-icon.trips {
  color: #087fca;

  background: #eaf6fc;
}

.summary-icon.scheduled {
  color: #079f91;

  background: #e8f8f6;
}

.summary-icon.unassigned {
  color: #c88700;

  background: #fff6dc;
}

.summary-icon.invalid {
  color: #d94b4b;

  background: #fff0f0;
}

.summary-content {
  display: flex;

  flex-direction: column;

  min-width: 0;
}

.summary-content span {
  color: #748196;

  font-size: 26px;

  font-weight: 600;
}

.summary-content strong {
  margin-top: 2px;

  color: var(--navy);

  font-size: 55px;

  line-height: 1.1;

  font-weight: 750;
}

.summary-content small {
  margin-top: 3px;

  color: #9aa5b2;

  font-size: 20px;
}

/* =========================================================
   STATE CARDS
   ========================================================= */
.state-card {
  min-height: 220px;

  display: flex;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  gap: 8px;

  padding: 30px;

  box-sizing: border-box;

  text-align: center;

  background: white;

  border: 1px solid var(--border);

  border-radius: 14px;

  color: var(--muted);
}

.state-card strong {
  color: var(--navy);

  font-size: 15px;
}

.state-card p {
  margin: 0;

  font-size: 12px;
}

.state-icon {
  width: 48px;
  height: 48px;

  display: flex;

  align-items: center;

  justify-content: center;

  margin-bottom: 4px;

  color: var(--primary);

  background: var(--primary-light);

  border-radius: 50%;

  font-size: 20px;
}

.error-state {
  background: #fffafa;

  border-color: #f3d0d0;
}

.error-state .state-icon {
  color: var(--danger);

  background: var(--danger-bg);
}

.error-state strong {
  color: #a93636;
}

.retry-btn {
  margin-top: 8px;

  padding: 8px 14px;

  color: white;

  background: var(--primary);

  border: 0;

  border-radius: 7px;

  font-size: 11px;

  font-weight: 600;

  cursor: pointer;
}

.state-spinner {
  width: 28px;
  height: 28px;

  margin-bottom: 8px;

  border: 3px solid #e5edf3;

  border-top-color: var(--primary);

  border-radius: 50%;

  animation: spin 0.7s linear infinite;
}

/* =========================================================
   CALENDAR CARD
   ========================================================= */
.calendar-card {
  overflow: hidden;

  background: white;

  border: 1px solid var(--border);

  border-radius: 14px;

  box-shadow: 0 2px 5px rgba(15, 23, 42, 0.03),
    0 10px 25px rgba(15, 23, 42, 0.04);
}

/* =========================================================
   CALENDAR HEADER
   ========================================================= */
.calendar-header {
  display: flex;

  align-items: center;

  justify-content: space-between;

  min-height: 72px;

  padding: 0 20px;

  border-bottom: 1px solid var(--border);
}

.calendar-header h2 {
  margin: 0 0 3px;

  color: var(--navy);

  font-size: 19px;

  font-weight: 700;
}

.calendar-header p {
  margin: 0;

  color: #8793a1;

  font-size: 11px;
}

.calendar-header-right {
  display: flex;

  align-items: center;

  gap: 16px;
}

.calendar-status {
  display: flex;

  align-items: center;

  gap: 6px;

  color: #667588;

  font-size: 10px;

  font-weight: 600;
}

.status-dot {
  width: 8px;
  height: 8px;

  border-radius: 50%;
}

.status-dot.blue {
  background: #087fca;
}

.status-dot.cyan {
  background: #08a79a;
}

.status-dot.yellow {
  background: #d99a17;
}

.status-dot.red {
  background: #d94b4b;
}

/* =========================================================
   CALENDAR GRID
   ========================================================= */
.calendar-row,
.calendar-body {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 10px;
  padding: 15px;
  border: 1px solid black;
  box-sizing: border-box;
}

.calendar-row {
  border: 1px solid rgb(170, 166, 166);
}

.weekdays {
  background: #f8fafc;

  border-bottom: 1px solid var(--border);
}

.weekdays div {
  padding: 11px 8px;

  color: #7a8797;

  text-align: center;

  font-size: 30px;

  font-weight: 700;

  letter-spacing: 0.7px;

  text-transform: uppercase;
}

.calendar-day {
  position: relative;

  min-height: 142px;

  padding: 11px;

  box-sizing: border-box;

  background: white;

  border: 1px solid rgb(131, 131, 131);

  cursor: pointer;

  opacity: 0;

  animation: calendarDayIn 0.3s ease-out forwards;

  transition: background 0.18s ease, box-shadow 0.18s ease;
}

.calendar-day:nth-child(7n) {
}

.calendar-day:hover {
  background: #f8fcff;

  box-shadow: inset 0 0 0 1px rgba(8, 127, 202, 0.18);

  z-index: 2;
}

.calendar-day.other-month {
  background: #fafbfc;
}

.calendar-day.other-month .day-number {
  color: #b8c1cb;
}

.calendar-day.today {
  background: #f5fbff;

  box-shadow: inset 3px 0 0 var(--primary);
}

.calendar-day.today .day-number {
  color: white;

  background: var(--primary);
}

.day-header {
  display: flex;

  align-items: center;

  justify-content: space-between;

  min-height: 28px;

  margin-bottom: 8px;
}

.day-number {
  width: 28px;
  height: 28px;

  display: flex;

  align-items: center;

  justify-content: right;

  border-radius: 50%;

  color: #475569;

  font-size: 20px;

  font-weight: 700;
}

.today-badge {
  padding: 3px 6px;

  color: var(--primary);

  background: var(--primary-light);

  border-radius: 4px;

  font-size: 8px;

  font-weight: 800;

  letter-spacing: 0.4px;
}

/* =========================================================
   DAY STATS
   ========================================================= */
.day-stats {
  display: flex;

  flex-direction: column;

  gap: 5px;
}

.stat-item {
  display: grid;

  grid-template-columns: 20px 1fr auto;

  align-items: center;

  gap: 5px;

  min-height: 25px;

  padding: 3px 6px;

  border-radius: 5px;

  font-size: 20px;
}

.stat-item .stat-icon {
  display: flex;

  align-items: center;

  justify-content: center;

  font-size: 20px;
}

.stat-item .stat-label {
  overflow: hidden;

  text-overflow: ellipsis;

  white-space: nowrap;

  font-weight: 600;
}

.stat-item strong {
  font-size: 20px;

  font-weight: 750;
}

/* Trips */
.trips-stat {
  color: #087fca;

  background: #edf8fd;
}

/* Scheduled */
.scheduled-stat {
  color: #07978b;

  background: #eaf8f6;
}

/* Warning */
.warning-stat {
  color: #b67a00;

  background: #fff7df;
}

/* Invalid */
.invalid-stat {
  color: #c94141;

  background: #fff0f0;
}

/* =========================================================
   EMPTY DAYS
   ========================================================= */
.empty-day {
  height: 82px;

  display: flex;

  align-items: center;

  justify-content: center;

  color: #c3cbd4;

  font-size: 10px;
}

.day-hover-hint {
  position: absolute;

  right: 9px;

  bottom: 7px;

  display: flex;

  align-items: center;

  gap: 4px;

  color: var(--primary);

  font-size: 8px;

  font-weight: 600;

  opacity: 0;

  transform: translateY(3px);

  transition: all 0.18s ease;
}

.calendar-day:hover .day-hover-hint {
  opacity: 1;

  transform: translateY(0);
}

/* =========================================================
   CALENDAR FOOTER
   ========================================================= */
.calendar-footer {
  display: flex;

  align-items: center;

  justify-content: space-between;

  min-height: 40px;

  padding: 0 16px;

  color: #8793a1;

  background: #fafbfc;

  border-top: 1px solid var(--border);

  font-size: 10px;
}

.calendar-footer span {
  display: flex;

  align-items: center;

  gap: 5px;
}

.calendar-footer i {
  color: var(--primary);
}

/* =========================================================
   MODAL
   ========================================================= */
.modal-overlay {
  position: fixed;

  inset: 0;

  z-index: 1000;

  display: flex;

  align-items: center;

  justify-content: center;

  padding: 20px;

  box-sizing: border-box;

  background: rgba(15, 23, 42, 0.62);

  backdrop-filter: blur(4px);
}

.modal-content {
  width: min(1500px, 95vw);

  max-height: 91vh;

  display: flex;

  flex-direction: column;

  overflow: hidden;

  background: white;

  border: 1px solid #e0e6ec;

  border-radius: 15px;

  box-shadow: 0 30px 70px rgba(15, 23, 42, 0.24);

  animation: modalIn 0.25s ease-out;
}

/* =========================================================
   MODAL HEADER
   ========================================================= */
.modal-header {
  display: flex;

  align-items: center;

  justify-content: space-between;

  flex-shrink: 0;

  padding: 15px 19px;

  border-bottom: 1px solid var(--border);
}

.modal-title-wrapper {
  display: flex;

  align-items: center;

  gap: 11px;
}

.modal-title-icon {
  width: 38px;
  height: 38px;

  display: flex;

  align-items: center;

  justify-content: center;

  color: white;

  background: var(--primary);

  border-radius: 9px;

  font-size: 15px;
}

.modal-header h3 {
  margin: 0 0 2px;

  color: var(--navy);

  font-size: 16px;

  font-weight: 700;
}

.modal-header p {
  margin: 0;

  color: #8793a1;

  font-size: 10px;
}

.close-btn {
  width: 32px;
  height: 32px;

  display: flex;

  align-items: center;

  justify-content: center;

  color: #667588;

  background: #f4f6f8;

  border: 1px solid #e1e6eb;

  border-radius: 7px;

  font-size: 13px;

  cursor: pointer;

  transition: all 0.18s ease;
}

.close-btn:hover {
  color: white;

  background: var(--danger);

  border-color: var(--danger);
}

/* =========================================================
   FILTER PANEL
   ========================================================= */
.orders-section {
  display: flex;

  flex-direction: column;

  min-height: 0;
}

.filter-panel {
  padding: 14px 40px 18px 40px;

  background: #f8fafc;

  border-bottom: 1px solid var(--border);

  flex-shrink: 0;
}

.filter-panel-header {
  display: flex;

  align-items: center;

  justify-content: space-between;

  margin-bottom: 11px;
}

.filter-panel-header h4 {
  display: inline-flex;

  align-items: center;

  gap: 6px;

  margin: 0;

  color: var(--navy);

  font-size: 12px;

  font-weight: 700;
}

.filter-panel-header h4 i {
  color: var(--primary);
}

.filter-panel-header span {
  margin-left: 8px;

  padding: 3px 7px;

  color: var(--primary);

  background: var(--primary-light);

  border-radius: 4px;

  font-size: 9px;

  font-weight: 700;
}

.clear-filters-btn {
  height: 30px;

  display: flex;

  align-items: center;

  gap: 5px;

  padding: 0 10px;

  color: #667588;

  background: white;

  border: 1px solid #dfe5eb;

  border-radius: 6px;

  font-size: 10px;

  font-weight: 600;

  cursor: pointer;
}

.clear-filters-btn:hover {
  color: var(--danger);

  border-color: #edc0c0;

  background: #fff8f8;
}

/* =========================================================
   FILTER GRID
   ========================================================= */
.filter-grid {
  display: grid;

  grid-template-columns: repeat(6, minmax(120px, 1fr));

  gap: 9px;
}

.filter-group {
  min-width: 0;
}

.filter-group label {
  display: block;

  margin-bottom: 4px;

  color: #000000;

  font-size: 12px;

  font-weight: 700;

  text-transform: uppercase;

  letter-spacing: 0.45px;
}

.input-with-icon {
  position: relative;
}

.input-with-icon > i {
  position: absolute;

  left: 9px;

  top: 50%;

  transform: translateY(-50%);

  color: #9ba7b4;

  font-size: 10px;

  pointer-events: none;
}

.filter-input {
  width: 100%;

  height: 35px;

  box-sizing: border-box;

  padding: 0 9px 0 28px;

  color: var(--text);

  background: white;

  border: 1px solid #dfe5eb;

  border-radius: 6px;

  font-size: 12px;

  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.filter-input:focus {
  outline: none;

  border-color: var(--primary);

  box-shadow: 0 0 0 3px rgba(8, 127, 202, 0.08);
}

.filter-input::placeholder {
  color: #0f0f0f;
}

select.filter-input {
  cursor: pointer;
}

.date-picker-input {
  cursor: pointer;
}

.date-picker-input.date-range-active {
  color: var(--primary);

  background: var(--primary-light);

  border-color: var(--primary);
}

/* =========================================================
   FILTER RESULT
   ========================================================= */
.filter-result {
  display: flex;

  align-items: center;

  gap: 5px;

  margin-top: 9px;

  padding-top: 8px;

  color: var(--primary);

  border-top: 1px dashed #dfe5eb;

  font-size: 10px;
}

.filter-result strong {
  font-weight: 750;
}

/* =========================================================
   MODAL SUMMARY
   ========================================================= */
.modal-summary {
  display: flex;

  align-items: center;

  gap: 7px;

  padding: 0px 18px;

  overflow-x: auto;

  border-bottom: 1px solid var(--border);

  flex-shrink: 0;
}

.summary-pill {
  display: flex;

  align-items: center;

  gap: 6px;

  flex-shrink: 0;

  padding: 5px 8px;

  color: #687789;

  background: #f7f9fb;

  border: 1px solid #e3e8ed;

  border-radius: 6px;

  font-size: 9px;
}

.summary-pill i {
  color: var(--primary);
}

.summary-pill strong {
  color: var(--navy);

  font-weight: 700;
}

.summary-pill.filtered {
  color: var(--primary);

  background: var(--primary-light);

  border-color: #cce8f6;
}

/* =========================================================
   TABLE
   ========================================================= */
.table-container {
  flex: 1;

  min-height: 0;

  overflow: auto;

  padding: 40px;

  border: 1px solid black;
}

.details-table {
  width: 100%;

  min-width: 950px;

  border-collapse: separate;

  border-spacing: 0;

  font-size: 18px;
}

.details-table th {
  position: sticky;

  top: 0;

  z-index: 5;

  padding: 10px 12px;

  color: #69788a;

  background: #f7f9fb;

  border-bottom: 1px solid #dfe5eb;

  text-align: left;

  font-size: 15px;

  font-weight: 700;

  text-transform: uppercase;

  letter-spacing: 0.4px;

  white-space: nowrap;
}

.details-table td {
  padding: 10px 12px;

  color: #435466;

  border-bottom: 1px solid #edf0f3;

  vertical-align: middle;
}

.details-table tbody tr {
  background: white;

  transition: background 0.15s ease;
}

.details-table tbody tr:hover {
  background: #f8fcff;
}

.invoice-number {
  color: var(--primary);

  font-weight: 700;
}

.customer-cell {
  display: flex;

  align-items: center;

  gap: 7px;
}

.customer-avatar {
  width: 25px;
  height: 25px;

  flex-shrink: 0;

  display: flex;

  align-items: center;

  justify-content: center;

  color: var(--primary);

  background: var(--primary-light);

  border-radius: 50%;

  font-size: 9px;
}

.address-cell {
  max-width: 280px;

  line-height: 1.4;
}

.date-highlight {
  color: #9a6900 !important;

  background: #fff8df;

  font-weight: 700;
}

/* =========================================================
   STATUS BADGES
   ========================================================= */
.status-badge {
  display: inline-flex;

  align-items: center;

  justify-content: center;

  min-width: 65px;

  padding: 5px 8px;

  border-radius: 5px;

  font-size: 14px;

  font-weight: 750;

  text-transform: uppercase;

  letter-spacing: 0.2px;
}

.status-new {
  color: #1769aa;

  background: #e9f4fd;
}

.status-paid {
  color: #167253;

  background: #e9f8f2;
}

.status-completed,
.status-finished,
.status-delivered {
  color: #087857;

  background: #e6f7ef;
}

.status-pending,
.status-returned {
  color: #9a6900;

  background: #fff6dc;
}

.status-shipped {
  color: #4d56a8;

  background: #eef0ff;
}

.status-cancelled,
.status-unable-to-complete {
  color: #b33d3d;

  background: #fff0f0;
}

/* =========================================================
   NO RESULTS
   ========================================================= */
.no-results-row td {
  padding: 50px 20px;

  text-align: center;
}

.no-results {
  display: flex;

  flex-direction: column;

  align-items: center;

  gap: 5px;

  color: #94a0ad;
}

.no-results i {
  margin-bottom: 5px;

  color: #b4bec8;

  font-size: 24px;
}

.no-results strong {
  color: #657386;

  font-size: 12px;
}

.no-results span {
  font-size: 10px;
}

/* =========================================================
   NO DETAILS
   ========================================================= */
.no-details {
  min-height: 260px;

  display: flex;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  gap: 6px;

  color: #8793a1;

  text-align: center;
}

.no-details strong {
  color: var(--navy);

  font-size: 14px;
}

.no-details span {
  font-size: 11px;
}

.no-details-icon {
  width: 50px;
  height: 50px;

  display: flex;

  align-items: center;

  justify-content: center;

  margin-bottom: 5px;

  color: #9aa6b3;

  background: #f2f5f7;

  border-radius: 50%;

  font-size: 21px;
}

/* =========================================================
   MODAL ERROR
   ========================================================= */
.modal-error {
  min-height: 230px;

  display: flex;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  gap: 7px;

  padding: 30px;

  color: #a63d3d;

  text-align: center;

  background: #fffafa;
}

.modal-error strong {
  font-size: 14px;
}

.modal-error p {
  max-width: 500px;

  margin: 0;

  color: #8b6464;

  font-size: 11px;
}

.modal-error-icon {
  width: 50px;
  height: 50px;

  display: flex;

  align-items: center;

  justify-content: center;

  color: var(--danger);

  background: var(--danger-bg);

  border-radius: 50%;

  font-size: 20px;
}

/* =========================================================
   TRUCK LOADING ANIMATION
   ========================================================= */
.truck-loading-container {
  flex-shrink: 0;

  background: #f5f8fa;
}

.truck-loading-scene {
  position: relative;

  height: 190px;

  overflow: hidden;

  background: linear-gradient(
    to bottom,
    #f5f8fb 0%,
    #f5f8fb 70%,
    #e9eef3 70%,
    #e9eef3 72%,
    #f5f8fb 72%
  );
}

/* Warehouse */
.warehouse {
  position: absolute;

  left: 25px;

  bottom: 43px;

  width: 120px;

  height: 76px;

  background: #475569;

  border-radius: 7px;

  z-index: 2;
}

.warehouse-roof {
  position: absolute;

  left: -7px;

  top: -9px;

  width: 134px;

  height: 10px;

  background: #334155;

  border-radius: 5px 5px 2px 2px;
}

.warehouse-door {
  position: absolute;

  left: 42px;

  bottom: 0;

  width: 37px;

  height: 50px;

  background: #1e293b;

  border-radius: 3px 3px 0 0;
}

.warehouse span {
  position: absolute;

  left: 50%;

  top: 10px;

  transform: translateX(-50%);

  color: #fbbf24;

  font-size: 7px;

  font-weight: 800;

  letter-spacing: 1px;
}

/* Packages */
.loading-package {
  position: absolute;

  z-index: 5;

  color: #c88700;

  font-size: 17px;

  animation: packageFloat 1s ease-in-out infinite;
}

.package-one {
  left: 75px;

  bottom: 46px;
}

.package-two {
  left: 98px;

  bottom: 47px;

  animation-delay: 0.2s;
}

.package-three {
  left: 55px;

  bottom: 46px;

  animation-delay: 0.4s;
}

@keyframes packageFloat {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-4px);
  }
}

/* Animated truck */
.animated-truck {
  position: absolute;

  left: -180px;

  bottom: 40px;

  width: 155px;

  height: 65px;

  z-index: 10;

  animation: truckDrive 7s cubic-bezier(0.45, 0, 0.2, 1) forwards;
}

.truck-body {
  position: absolute;

  left: 0;

  bottom: 0;

  width: 108px;

  height: 43px;

  background: #1e40af;

  border-radius: 7px 7px 4px 4px;
}

.truck-body::after {
  content: '';

  position: absolute;

  left: 18px;

  top: 14px;

  width: 58px;

  height: 18px;

  border: 2px solid rgba(255, 255, 255, 0.25);

  border-radius: 3px;
}

.truck-cabin {
  position: absolute;

  right: 5px;

  bottom: 0;

  width: 52px;

  height: 53px;

  background: #172554;

  border-radius: 7px 7px 4px 4px;
}

.truck-window {
  position: absolute;

  right: 8px;

  top: 9px;

  width: 25px;

  height: 23px;

  background: #67e8f9;

  border-radius: 4px;
}

.truck-wheel {
  position: absolute;

  bottom: -8px;

  width: 20px;

  height: 20px;

  background: #111827;

  border: 3px solid #334155;

  border-radius: 50%;

  animation: wheelRotate 0.45s linear infinite;
}

.truck-wheel-one {
  left: 20px;
}

.truck-wheel-two {
  right: 17px;
}

.truck-light {
  position: absolute;

  right: 0;

  bottom: 13px;

  width: 7px;

  height: 7px;

  background: #fef08a;

  border-radius: 50%;

  box-shadow: 0 0 10px #fde047;
}

.loading-road {
  position: absolute;

  left: 0;

  right: 0;

  bottom: 37px;

  height: 3px;

  background: #cbd5e1;
}

/* Loading message */
.truck-loading-message {
  position: absolute;

  left: 50%;

  bottom: 10px;

  transform: translateX(-50%);

  display: flex;

  align-items: center;

  gap: 7px;

  padding: 7px 13px;

  color: #536273;

  background: white;

  border: 1px solid #e0e6eb;

  border-radius: 20px;

  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.08);

  font-size: 10px;

  font-weight: 600;

  white-space: nowrap;

  z-index: 20;
}

.truck-loading-message i {
  color: var(--primary);
}

.loading-dots-inline {
  display: flex;

  gap: 1px;
}

.loading-dots-inline span {
  animation: loadingDot 1.2s infinite;
}

.loading-dots-inline span:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots-inline span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes loadingDot {
  0%,
  60%,
  100% {
    opacity: 0.3;
  }

  30% {
    opacity: 1;
  }
}

/* =========================================================
   MAIN LOADING OVERLAY
   ========================================================= */
.loading-overlay {
  position: fixed;

  inset: 0;

  z-index: 2000;

  display: flex;

  align-items: center;

  justify-content: center;

  background: rgba(15, 23, 42, 0.58);

  backdrop-filter: blur(5px);
}

.loading-card {
  width: min(380px, 90vw);

  padding: 35px;

  box-sizing: border-box;

  text-align: center;

  background: white;

  border-radius: 15px;

  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.22);

  animation: loadingCardIn 0.25s ease-out;
}

.loading-animation {
  margin-bottom: 20px;
}

.loading-ring {
  position: relative;

  width: 75px;
  height: 75px;

  margin: 0 auto;

  display: flex;

  align-items: center;

  justify-content: center;
}

.ring {
  position: absolute;

  inset: 0;

  border: 3px solid transparent;

  border-radius: 50%;

  animation: ringRotate 1.2s linear infinite;
}

.ring:nth-child(1) {
  border-top-color: var(--primary);

  border-left-color: var(--primary);
}

.ring:nth-child(2) {
  inset: 10px;

  border-right-color: #16a085;

  border-bottom-color: #16a085;

  animation-direction: reverse;
}

.ring:nth-child(3) {
  inset: 20px;

  border-left-color: #e1a126;

  border-bottom-color: #e1a126;
}

.loading-logo {
  color: var(--primary);

  font-size: 22px;
}

.loading-text {
  display: flex;

  align-items: center;

  justify-content: center;

  margin-bottom: 18px;
}

.loading-text h3 {
  margin: 0;

  color: var(--navy);

  font-size: 15px;

  font-weight: 700;
}

.loading-dots {
  display: flex;

  gap: 3px;

  margin-left: 7px;
}

.loading-dots .dot {
  width: 5px;
  height: 5px;

  background: var(--primary);

  border-radius: 50%;

  animation: dotBounce 1.2s ease-in-out infinite;
}

.loading-dots .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots .dot:nth-child(3) {
  animation-delay: 0.4s;
}

.loading-progress {
  width: 100%;

  height: 5px;

  margin-bottom: 15px;

  overflow: hidden;

  background: #e9edf1;

  border-radius: 5px;
}

.progress-bar {
  height: 100%;

  background: var(--primary);

  border-radius: inherit;

  transition: width 0.25s ease;
}

.tip-text {
  margin: 0;

  color: #8290a0;

  font-size: 10px;
}

/* =========================================================
   SMALL SPINNER
   ========================================================= */
.spinner-sm {
  width: 13px;
  height: 13px;

  display: inline-block;

  border: 2px solid rgba(255, 255, 255, 0.35);

  border-top-color: white;

  border-radius: 50%;

  animation: spin 0.6s linear infinite;
}

/* =========================================================
   ANIMATIONS
   ========================================================= */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes ringRotate {
  to {
    transform: rotate(360deg);
  }
}

@keyframes dotBounce {
  0%,
  60%,
  100% {
    transform: translateY(0);
  }

  30% {
    transform: translateY(-5px);
  }
}

@keyframes wheelRotate {
  to {
    transform: rotate(360deg);
  }
}

@keyframes truckDrive {
  0% {
    left: -180px;
    opacity: 0;
  }

  10% {
    opacity: 1;
  }

  25% {
    left: 150px;
  }

  45% {
    left: 250px;
  }

  65% {
    left: 400px;
  }

  82% {
    left: 600px;
  }

  100% {
    left: calc(100% + 30px);
    opacity: 0;
  }
}

@keyframes calendarDayIn {
  from {
    opacity: 0;
    transform: translateY(5px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes modalIn {
  from {
    opacity: 0;
    transform: translateY(15px) scale(0.99);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes loadingCardIn {
  from {
    opacity: 0;
    transform: scale(0.96);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* =========================================================
   TABLE SCROLLBAR
   ========================================================= */
.table-container::-webkit-scrollbar {
  width: 7px;
  height: 7px;
}

.table-container::-webkit-scrollbar-track {
  background: #f4f6f8;
}

.table-container::-webkit-scrollbar-thumb {
  background: #c5ced7;

  border-radius: 10px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #aeb9c4;
}

/* =========================================================
   RESPONSIVE — 1200
   ========================================================= */
@media (max-width: 1200px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .filter-grid {
    grid-template-columns: repeat(3, minmax(120px, 1fr));
  }
}

/* =========================================================
   RESPONSIVE — 900
   ========================================================= */
@media (max-width: 900px) {
  .delivery-calendar-wrapper {
    padding: 16px;
  }

  .page-header {
    align-items: flex-start;

    flex-direction: column;
  }

  .header-controls {
    width: 100%;
  }

  .control-wrapper {
    flex: 1;
  }

  .month-picker-input,
  .location-select {
    width: 100%;
  }

  .refresh-btn {
    flex-shrink: 0;
  }

  .calendar-header-right {
    display: none;
  }

  .calendar-day {
    min-height: 125px;

    padding: 9px;
  }
}

/* =========================================================
   RESPONSIVE — 768
   ========================================================= */
@media (max-width: 768px) {
  .delivery-calendar-wrapper {
    padding: 10px;
  }

  .page-heading h1 {
    font-size: 20px;
  }

  .header-controls {
    display: grid;

    grid-template-columns: 1fr 1fr;
  }

  .refresh-btn {
    width: 100%;
  }

  .summary-grid {
    grid-template-columns: 1fr 1fr;

    gap: 9px;
  }

  .summary-card {
    min-height: 82px;

    padding: 11px;
  }

  .summary-icon {
    width: 38px;
    height: 38px;

    font-size: 15px;
  }

  .summary-content strong {
    font-size: 20px;
  }

  .calendar {
    overflow-x: auto;
  }

  .calendar-card {
    overflow-x: auto;
  }

  .calendar-header,
  .weekdays,
  .calendar-body,
  .calendar-footer {
    min-width: 720px;
  }

  .modal-overlay {
    padding: 8px;
  }

  .modal-content {
    width: 100%;

    max-height: 95vh;
  }

  .filter-grid {
    grid-template-columns: 1fr 1fr;
  }

  .filter-panel {
    overflow-y: auto;

    max-height: 300px;
  }

  .table-container {
    overflow: auto;
    padding: 20px;
  }
}

/* =========================================================
   RESPONSIVE — 500
   ========================================================= */
@media (max-width: 500px) {
  .header-controls {
    grid-template-columns: 1fr;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .page-header {
    padding: 16px;
  }

  .page-icon {
    width: 40px;
    height: 40px;

    font-size: 17px;
  }

  .calendar-day {
    min-height: 115px;
  }

  .filter-grid {
    grid-template-columns: 1fr;
  }

  .modal-summary {
    flex-wrap: wrap;
  }
}

/* =========================================================
   REDUCED MOTION
   ========================================================= */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;

    animation-iteration-count: 1 !important;

    transition-duration: 0.01ms !important;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/AdminToolsDeliveryCalendar.vue